import React, {
  createContext,
  PropsWithChildren,
  useMemo,
  useState,
  useContext,
} from 'react';
import { Values } from '../../../modules/reports/types';
import { empty } from '../../../utils/array';
import scheduleMicrotask from '../../../utils/schedule-microtask';
import { useFiltersQueryParams } from '../../../utils/use-data-params';
import useLocalStorageState from 'use-local-storage-state';
import { useShallowEqualEffect } from '../../../utils/use-shallow-effect';
import { uniq } from 'lodash';

type BasicValue = string | number | boolean | unknown | BasicValue[];

export type BaseValues = {
  [key: string]: BasicValue;
};

type FiltersContextType<Values extends object = BaseValues> = {
  values: Values;
  setValues(values: Values): void;
  remove(key: keyof Values): void;
  setValue(key: keyof Values, value: BasicValue): void;
  committedValues: Values;
  commitValues(values: Values): void;
  enabled: string[];
  setEnabled(items: string[] | ((prev: string[]) => string[])): void;
  hasActiveFilters(): boolean;
};

const FiltersContext = createContext<FiltersContextType>({
  values: {},
  setValues(values: Values) {
    throw new Error('FiltersContextProvider not provided.');
  },
  setValue(key: keyof Values, value: BasicValue) {
    throw new Error('FiltersContextProvider not provided.');
  },
  remove(key: keyof Values) {},
  committedValues: {},
  commitValues() {},
  enabled: [],
  setEnabled() {},
  hasActiveFilters() {
    return false;
  },
});

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error(
      'FiltersContext is null. Did you forget to use FiltersContextProvider?',
    );
  }

  return context;
};

const getActiveFilters = (params: any) => {
  return Object.keys(params).filter((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      return !empty(value);
    }

    return Boolean(value);
  });
};

export const FiltersContextProvider = ({
  children,
  defaultValues = {},
  defaultEnabled = [],
  persistKey = '',
  onChange,
  onEnabledChange,
}: PropsWithChildren<{
  defaultValues?: Values;
  defaultEnabled?: string[];
  persistKey?: string;
  onChange?(values: Values): void;
  onEnabledChange?(enabled: (keyof Values)[]): void;
}>) => {
  const [params, setQueryParams] = useFiltersQueryParams(defaultValues);
  const [values, setValues] = useState<Values>(params);
  const defaultEnabledFilters = uniq(
    defaultEnabled.concat(getActiveFilters(params)),
  );

  const [enabled, setEnabled] = persistKey
    ? useLocalStorageState(persistKey, { defaultValue: defaultEnabledFilters })
    : useState(defaultEnabledFilters);

  const commitValues = (values: Values) => {
    setQueryParams(values);
    scheduleMicrotask(() => {
      // setEnabled(Object.keys(values));
      setValues(values);
    });
  };

  useShallowEqualEffect(() => {
    onChange?.(values);
  }, [values]);

  useShallowEqualEffect(() => {
    onEnabledChange?.(enabled);
  }, [enabled]);

  const contextValue = useMemo<FiltersContextType>(
    () => ({
      values,
      setValues,
      setValue(key: keyof Values, value: BasicValue) {
        setValues((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
      remove(key) {
        setValues((prev) => {
          const { [key]: value, ...next } = prev;

          return next;
        });
      },
      committedValues: params,
      commitValues,
      enabled,
      setEnabled,
      hasActiveFilters(): boolean {
        return !empty(enabled);
      },
    }),
    [values, setValues, params, enabled, setEnabled],
  );

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};
